/**
 * "Realizacje" / "Case studies" — list and detail.
 *
 * The mock-up only had the list; the detail page follows the same section
 * grammar (hero → challenge/solution/outcome → metrics → related services) so a
 * card is not a dead end.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs, eyebrow, sectionHead, statBlock } from "../components/common.js";
import { caseCard, relatedServiceCard } from "../components/cards.js";
import { notFoundView } from "./status.js";

export async function caseStudiesListView(ctx: AppContext): Promise<HTMLElement> {
	const copy = ctx.copy();
	const studies = await ctx.gateway.caseStudies();

	applyPageSeo(ctx, {
		title: `${copy.nav.cases} — ${copy.brand.name}`,
		description: copy.cases.lead,
	});

	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const study of studies) grid.appendChild(caseCard(ctx, study));

	return el(
		"div",
		{ class: "mb-view mb-view--cases" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: copy.nav.cases }]),
				el("h1", { class: "mb-pagehead__title" }, copy.cases.title),
				el("p", { class: "mb-pagehead__lead" }, copy.cases.lead),
				grid,
			),
		),
	);
}

export async function caseStudyDetailView(
	ctx: AppContext,
	match: RouteMatch,
): Promise<HTMLElement> {
	const copy = ctx.copy();
	// Set by the /podglad dispatcher (main.ts): read the draft, and keep the
	// page out of every index.
	const preview = match.query.preview === "true";
	// The router's page (for a preview: /podglad/…, not this synthetic match),
	// taken before the reads so the alternates cannot land on a later page.
	const page = ctx.router.getCurrent();
	const study = await ctx.gateway.caseStudy(match.params.slug ?? "", preview);
	if (!study) return notFoundView(ctx);

	// Each language has its own slug, so the PL/EN switch and hreflang need the
	// other one: without it they led to a 404 (/case-studies/{polish-slug}).
	// No version there: the switch goes to that language's list. A draft
	// preview only follows a slug it found and otherwise keeps its own.
	const otherLocale = ctx.locale() === "pl" ? "en" : "pl";
	const slugThere = await ctx.gateway.caseStudySlugIn(study, otherLocale);
	if (slugThere || !preview) {
		ctx.router.setAlternates(
			page,
			{ [otherLocale]: slugThere ? { slug: slugThere } : null },
			"caseStudies",
		);
	}

	applyPageSeo(ctx, {
		title: `${study.title} — ${copy.brand.name}`,
		description: study.outcome,
		noindex: preview,
	});

	const isEn = ctx.locale() === "en";
	const view = el("div", { class: "mb-view mb-view--case" });

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [
					{ label: copy.nav.start, key: "home" },
					{ label: copy.nav.cases, key: "caseStudies" },
					{ label: study.title },
				]),
				el("h1", { class: "mb-pagehead__title" }, study.title),
				el("p", { class: "mb-pagehead__lead" }, study.outcome),
				el(
					"div",
					{ class: "mb-casemetrics" },
					// `metrics` is a repeatable Strapi component: LIVE omits it unless the
					// gateway populates it, so treat it as possibly absent.
					...(study.metrics ?? []).map((metric) => statBlock(metric.value, metric.label)),
				),
			),
		),
	);

	if (study.heroImage) {
		view.appendChild(
			el(
				"section",
				{ class: "mb-section mb-section--white" },
				el(
					"div",
					{ class: "mb-section__inner" },
					el("img", {
						class: "mb-photo mb-photo--wide",
						src: study.heroImage.url,
						alt: study.heroImage.alt ?? study.title,
						loading: "lazy",
						decoding: "async",
					}),
				),
			),
		);
	}

	const blocks: [string, string][] = [
		[isEn ? "Challenge" : "Wyzwanie", study.challenge],
		[isEn ? "Solution" : "Rozwiązanie", study.solution],
		[isEn ? "Outcome" : "Efekt", study.outcome],
	];

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner mb-grid mb-grid--3" },
				...blocks.map(([heading, body]) =>
					el(
						"article",
						{ class: "mb-caseblock" },
						eyebrow(heading),
						el("p", { class: "mb-prose" }, body),
					),
				),
			),
		),
	);

	// The detail route uses `CaseStudyFull`, which does select `services` — but the
	// same component renders from cached list data, where the key is absent. Treat
	// missing as "no related services" rather than crashing the route.
	const relatedServices = study.services ?? [];
	if (relatedServices.length > 0) {
		const grid = el("div", { class: "mb-grid mb-grid--3" });
		for (const service of relatedServices) grid.appendChild(relatedServiceCard(ctx, service));
		view.appendChild(
			el(
				"section",
				{ class: "mb-section mb-section--paper" },
				el(
					"div",
					{ class: "mb-section__inner" },
					sectionHead(ctx, {
						title: isEn ? "Services used" : "Wykorzystane usługi",
						link: { label: copy.serviceDetail.relatedAll, key: "services" },
						className: "mb-sectionhead--row",
					}),
					grid,
				),
			),
		);
	}

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner mb-endcta" },
				el("h2", { class: "mb-endcta__title" }, copy.article.ctaTitle),
				el("p", { class: "mb-endcta__desc" }, copy.article.ctaDesc),
				el(
					"div",
					{ class: "mb-endcta__actions" },
					el(
						"a",
						{
							class: "mb-cta mb-cta--accent",
							href: ctx.href("contact"),
							"data-routekey": "contact",
						},
						copy.article.ctaConsult,
					),
					el(
						"a",
						{
							class: "mb-cta mb-cta--outline",
							href: ctx.href("caseStudies"),
							"data-routekey": "caseStudies",
						},
						copy.nav.cases,
					),
				),
			),
		),
	);

	return view;
}
